import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { displayToast } from "../../components/app/AppToast";
import type { OauthRoomsConfig } from ".";

/** What the caller still has to do once the flow is over. */
export type RoomOAuthOutcome =
  /**
   * The sheet or the redirect owns the screen now, or the app has already
   * routed to the callback page. The caller renders nothing new.
   */
  | { kind: "started" }
  /**
   * The reader came back without signing in, and is looking at whatever the
   * caller left on screen. The caller MUST put its gate back: before the sheet
   * existed this could not happen, because the page navigated away and never
   * returned. A caller that ignores this strands the reader.
   */
  | { kind: "abandoned" };

/**
 * Starts the partner OAuth flow for one room and routes what comes back.
 *
 * Every entry point into a partner-gated room (the room page, an invite link,
 * the onboarding flow) needs the same three steps: build the
 * redirect URI, hand the localized sheet copy to the manager, then act on the
 * outcome. They live here so the three callers cannot drift apart.
 *
 * What the caller cannot delegate is the screen it leaves behind, so the two
 * outcomes that put the reader back on it are returned rather than swallowed.
 */
export function useStartRoomOAuth(): (
  config: (typeof OauthRoomsConfig)[number],
  roomId: string
) => Promise<RoomOAuthOutcome> {
  const { t } = useTranslation();
  const history = useHistory();

  return async (config, roomId) => {
    const redirectUri = `${globalThis.location.origin}/oauth/callback/${roomId}`;

    const started = await config.manager.startAuth(redirectUri, {
      title: config.name,
      // Keys that already carry all eight locales. A partner-specific wording
      // would need a translation pass this change does not own.
      errorTitle: t("Something went wrong"),
      errorMessage: t("Unable to load content."),
      retry: t("Try again"),
      close: t("Close"),
    });

    switch (started.kind) {
      case "redirecting":
        // The browser is leaving this page. Nothing left to render.
        return { kind: "started" };
      case "callback":
        history.replace(started.target);
        return { kind: "started" };
      case "cancelled":
        // The reader closed the sheet on purpose, so no toast. The caller
        // still has to restore what it hid on the way out.
        return { kind: "abandoned" };
      case "failed":
        console.error(`partner OAuth session failed: ${started.reason}`);
        displayToast(t("Something went wrong. Please try again."), "error");
        return { kind: "abandoned" };
      default:
        throw new Error(started satisfies never);
    }
  };
}
